import { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { partnersService, Partner } from '../services/partners';
import { getApiOrigin } from '../lib/api-url';

const MIN_LOOP_ITEMS = 8;

function partnerLogoSrc(logoUrl: string | undefined, base: string): string {
  if (!logoUrl) return '';
  return logoUrl.startsWith('http') ? logoUrl : `${base}${logoUrl}`;
}

function PartnerLogoCard({ partner, baseUrl }: { partner: Partner; baseUrl: string }) {
  const src = partnerLogoSrc(partner.logoUrl, baseUrl);
  const [imgFailed, setImgFailed] = useState(false);
  const showName = !src || imgFailed;

  return (
    <div
      className="mx-6 flex h-24 w-40 shrink-0 items-center justify-center bg-transparent sm:mx-8 sm:h-28 sm:w-48 md:h-32 md:w-56"
      title={partner.name}
    >
      {showName ? (
        <span className="text-center font-serif text-sm font-semibold uppercase tracking-[0.15em] text-gray-600">
          {partner.name}
        </span>
      ) : (
        <img
          src={src}
          alt={partner.name}
          loading="lazy"
          decoding="async"
          className="max-h-full max-w-full object-contain"
          onError={() => setImgFailed(true)}
        />
      )}
    </div>
  );
}

function buildLoopList(partners: Partner[]): Partner[] {
  if (partners.length === 0) return [];
  const repeats = Math.max(2, Math.ceil(MIN_LOOP_ITEMS / partners.length));
  return Array.from({ length: repeats }, () => partners).flat();
}

export default function PartnerLogosStrip() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    partnersService
      .getAll()
      .then((data) => {
        setPartners(
          data
            .filter((p) => p.isActive && (p.logoUrl || p.name))
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
        );
      })
      .catch((err) => console.error('Failed to load partners', err))
      .finally(() => setLoading(false));
  }, []);

  const baseUrl = getApiOrigin();
  const loopList = useMemo(() => buildLoopList(partners), [partners]);

  const marqueeDuration = useMemo(() => {
    const base = Math.max(partners.length, 1) * 9;
    return `${Math.min(Math.max(base, 18), 48)}s`;
  }, [partners.length]);

  if (loading) {
    return (
      <section className="relative overflow-hidden border-y border-stone-100 bg-white py-16" aria-hidden>
        <div className="mx-auto h-24 max-w-4xl animate-pulse rounded-lg bg-stone-100/60" />
      </section>
    );
  }

  if (partners.length === 0) return null;

  return (
    <section
      className="relative overflow-hidden border-y border-stone-100 bg-white py-16 sm:py-20"
      aria-labelledby="partners-heading"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-12 text-center sm:mb-14">
          <div className="mb-5 flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-gradient-to-r from-transparent to-accent-400/80 sm:w-20" aria-hidden />
            <Sparkles className="h-4 w-4 text-accent-500" strokeWidth={1.5} aria-hidden />
            <span className="h-px w-12 bg-gradient-to-l from-transparent to-accent-400/80 sm:w-20" aria-hidden />
          </div>
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.35em] text-primary-500 sm:text-[11px]">
            Références & collaborations
          </p>
          <h2
            id="partners-heading"
            className="font-serif text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl md:text-[2rem]"
          >
            Ils nous confient leur image
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-gray-500">
            Des marques et institutions qui nous choisissent pour sublimer leur communication visuelle.
          </p>
        </header>

        {reducedMotion ? (
          <div className="flex flex-wrap items-center justify-center gap-10">
            {partners.map((partner) => (
              <PartnerLogoCard key={partner.id} partner={partner} baseUrl={baseUrl} />
            ))}
          </div>
        ) : (
          <div className="relative -mx-4 overflow-hidden sm:-mx-6">
            <div
              className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent sm:w-24"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent sm:w-24"
              aria-hidden
            />

            <div className="flex w-max items-center py-2">
              <div
                className="flex shrink-0 items-center"
                style={{ animation: `partner-marquee-lux ${marqueeDuration} linear infinite` }}
              >
                {loopList.map((partner, idx) => (
                  <PartnerLogoCard
                    key={`a-${partner.id}-${idx}`}
                    partner={partner}
                    baseUrl={baseUrl}
                  />
                ))}
              </div>
              <div
                className="flex shrink-0 items-center"
                aria-hidden
                style={{ animation: `partner-marquee-lux ${marqueeDuration} linear infinite` }}
              >
                {loopList.map((partner, idx) => (
                  <PartnerLogoCard
                    key={`b-${partner.id}-${idx}`}
                    partner={partner}
                    baseUrl={baseUrl}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes partner-marquee-lux {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .partner-marquee-track > div {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
}
